import React, { useState, useEffect, useRef } from 'react';
import ReusableTable from '../../dashboard/ReusableTable';
import ReusableForm from '../../common/ReusableForm';
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from '../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BlockManagement = () => {
  const [blocks, setBlocks] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [currentBlock, setCurrentBlock] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const formFields = [
    { name: 'name', label: 'Block Name', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text' }
  ];

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // Fetch blocks function
  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/mpr-block-master/?search=${encodeURIComponent(search)}&page=${currentPage}`);
      const blocksArray = response.results?.data || [];
      setBlocks(blocksArray);
      setTotalRecords(response.count || response.results?.count || 0);
    } catch (error) {
      toast.error('Failed to fetch blocks.');
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect for table search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      fetchBlocks();
    }, 700);
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const handleSaveBlock = async () => {
    try {
      if (editMode && currentBlock) {
        // Use POST for update with id in payload
        await postRequest('master/mpr-block-master/', { 
          ...formData, 
          id: currentBlock.id 
        });
        toast.success('Block updated successfully!');
      } else {
        // Use POST for create
        await postRequest('master/mpr-block-master/', formData);
        toast.success('Block added successfully!');
        setCurrentPage(1);
      }
      await fetchBlocks();
      setShowFormModal(false);
      setEditMode(false);
      setCurrentBlock(null);
      setFormData({ name: '', description: '' });
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save block.');
      }
    }
  };

  const openAddModal = () => {
    setFormData({ name: '', description: '' });
    setEditMode(false);
    setCurrentBlock(null);
    setShowFormModal(true);
  };

  const openEditModal = (block) => {
    setCurrentBlock(block);
    setFormData({ name: block.name, description: block.description });
    setEditMode(true);
    setShowFormModal(true);
  };

  const openDeleteModal = (block) => {
    setBlockToDelete(block);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteModal(true);
  };

  const deleteBlock = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.error('Please type "DELETE" to confirm');
      return;
    }
    try {
      // Use POST for delete with status_db=3
      await postRequest('master/mpr-block-master/', { 
        id: blockToDelete.id, 
        status_db: 3 
      });
      toast.success('Block deleted successfully!');
      await fetchBlocks();
      setShowDeleteModal(false);
      setBlockToDelete(null);
      setDeleteFormData({ confirmation: '' });
    } catch (error) {
      toast.error('Failed to delete block.');
    }
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const tableColumns = [
    { field: 'name', header: 'Name' },
    { field: 'description', header: 'Description' }
  ];

  const actionItems = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (rowData) => openEditModal(rowData),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  return (
    <div className="bg-gray-100 py-8 px-4">
      <ToastContainer />
      <div className="max-w-full mx-auto">
        <ReusableTable
          title="Block Details"
          data={blocks}
          setData={setBlocks}
          columnFields={tableColumns}
          actionMenu={true}
          actionItems={actionItems}
          loading={loading}
          totalRecords={totalRecords}
          rows={rowsPerPage}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          searchValue={search}
          onSearchChange={handleTableSearch}
          setAddNewDialogVisible={openAddModal}
        />

        {/* Add/Edit Modal with explicit wrapper */}
        {showFormModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-lg animate-fade-in">
              <ReusableForm
                headerText={editMode ? "Edit Block" : "Add Block"}
                formData={formData}
                setFormData={setFormData}
                fields={formFields}
                onSubmit={handleSaveBlock}
                onHide={() => {
                  setShowFormModal(false);
                  setEditMode(false);
                  setCurrentBlock(null);
                  setFormData({ name: '', description: '' });
                }}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ReusableDeleteDialog
          visible={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onDelete={deleteBlock}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Block"
          description={`Are you sure you want to delete block "${blockToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
        />
      </div>
    </div>
  );
};

export default BlockManagement;